import { Label } from "@radix-ui/react-label";
import { useId } from "react";
import { cn } from "@/lib/utils";
import ComboBox, {
    ComboboxItem,
    QueryFnParams,
    PaginationResponse,
} from "../combo-box/ComboBox";

interface ArFilterComboboxProps<TItem extends ComboboxItem> {
    label: string;
    labelAlign?: "items-end" | "items-start" | "items-center";
    queryKey: string | string[];
    queryFn: (params: QueryFnParams) => Promise<PaginationResponse<TItem>>;
    additionalFilters?: Record<string, any>;
    // Display configuration
    getDisplayValue: (item: TItem) => string;
    renderItem?: (item: TItem) => React.ReactNode;
    // UI customization
    placeholder?: string;
    searchPlaceholder?: string;
    className?: string;
    width?: string;
    disabled?: boolean;
    onItemSelect?: (item: TItem | null) => void;
    // Query options
    pageSize?: number;
    debounceMs?: number;
    // Initial value (ID of selected item)
    defaultValueId?: string;
    defaultValue?: TItem;
}

export function ArFilterCombobox<TItem extends ComboboxItem>({
    label,
    labelAlign,
    queryKey,
    queryFn,
    additionalFilters,
    getDisplayValue,
    renderItem,
    placeholder = "Select item...",
    searchPlaceholder = "Search...",
    className,
    width,
    disabled,
    pageSize,
    debounceMs,
    onItemSelect,
    defaultValueId,
    defaultValue,
}: ArFilterComboboxProps<TItem>) {
    const id = useId();

    const handleSelect = (item: TItem | null) => {
        if (onItemSelect) {
            onItemSelect(item);
        }
    };

    return (
        <div className={cn("flex flex-col gap-2", labelAlign && labelAlign)}>
            <Label htmlFor={id} className={cn("text-sm font-medium")}>
                {label}
            </Label>
            <ComboBox<TItem>
                defaultValue={defaultValue}
                queryKey={queryKey}
                queryFn={queryFn}
                additionalFilters={additionalFilters}
                getDisplayValue={getDisplayValue}
                renderItem={renderItem}
                onSelect={handleSelect}
                initialValue={defaultValueId}
                initialItem={defaultValue}
                placeholder={placeholder}
                searchPlaceholder={searchPlaceholder}
                className={cn(className)}
                width={width}
                disabled={disabled}
                pageSize={pageSize}
                debounceMs={debounceMs}
            />
        </div>
    );
}

export default ArFilterCombobox;
